/**
 * HospitaisPage - Refactored with hooks and sub-components
 *
 * Original: 2,246 lines
 * Refactored: ~200 lines
 *
 * Extracted:
 * - 6 custom hooks (useHospitalData, useHospitalForm, useHospitalModals, useHospitalLocais, useHospitalExcel, useGeoData)
 * - 8 sub-components (HospitalHeader, HospitalTabs, DadosGeraisTab, LocaisContagemTab, RepresentantesTab, LocalModal, ImportHospitalModal, ImportLocaisModal)
 */
import React, { useState } from 'react';
import { Sidebar } from '../components/Sidebar';
import { MainLayout } from '../components/MainLayout';
import { useConfirm } from '../hooks/useConfirm';
import { msg, MESSAGES } from '../utils/messages';
import estabelecimentoService from '../services/estabelecimentoService';
import ImportHospitalRepresentantesModal from '../components/ImportHospitalRepresentantesModal';

// Custom hooks
import {
  useHospitalData,
  useHospitalForm,
  useHospitalModals,
  useHospitalLocais,
  useHospitalExcel,
  useGeoData,
} from '../hooks';

// Sub-components
import {
  HospitalHeader,
  HospitalTabs,
  DadosGeraisTab,
  LocaisContagemTab,
  RepresentantesTab,
  LocalModal,
  ImportHospitalModal,
  ImportLocaisModal,
  BulkImportLocaisModal,
  type HospitalTabType,
} from '../components/hospital';

const HospitaisPage: React.FC = () => {
  // Tab state
  const [activeTab, setActiveTab] = useState<HospitalTabType>('dados-gerais');

  // Confirm dialog
  const { confirm, ConfirmDialog } = useConfirm();

  // Data hook
  const {
    hospitais,
    selectedHospital,
    setSelectedHospital,
    filteredHospitais,
    loading,
    searchTerm,
    setSearchTerm,
    isMaster,
    showInactive,
    setShowInactive,
    reactivateHospital,
    locaisContagem,
    representantes,
    refreshHospitais,
    refreshLocais,
    refreshRepresentantes,
    updateHospitalInList,
    addHospitalToList,
    removeHospitalFromList,
    token,
  } = useHospitalData();

  // Form hook
  const {
    isEditing,
    editedHospital,
    setEditedHospital,
    saving,
    currentHospital,
    handleEdit,
    handleCreateNew,
    handleCancel,
    handleFieldChange,
    handleSave,
  } = useHospitalForm({
    selectedHospital,
    setSelectedHospital,
    updateHospitalInList,
    addHospitalToList,
  });

  // Modals hook
  const {
    showLocalModal,
    setShowLocalModal,
    showImportRepresentantesModal,
    setShowImportRepresentantesModal,
    showImportHospitaisModal,
    setShowImportHospitaisModal,
    showImportLocaisModal,
    setShowImportLocaisModal,
    showBulkImportLocaisModal,
    setShowBulkImportLocaisModal,
    showHospitalDropdown,
    toggleHospitalDropdown,
    closeHospitalDropdown,
  } = useHospitalModals();

  // Locais hook
  const {
    newLocal,
    updateLocalField,
    savingLocal,
    editingLocal,
    handleEditLocal,
    handleSaveLocal,
    handleDeleteLocal,
    handleCloseLocalModal,
    // Story 2.34: contatos extras
    contatosExtras,
    handleAddContatoExtra,
    handleUpdateContatoExtraField,
    handleRemoveContatoExtra,
  } = useHospitalLocais({
    selectedHospital,
    token,
    refreshLocais,
    setShowLocalModal,
  });

  // Excel hook
  const {
    importFile,
    setImportFile,
    importResult,
    handleDownloadTemplate,
    handleImportHospitais,
    handleExportExcel,
    resetImportState,
    importLocaisFile,
    setImportLocaisFile,
    importLocaisResult,
    handleDownloadLocaisTemplate,
    handleImportLocais,
    handleExportLocais,
    resetImportLocaisState,
    bulkImportLocaisFile,
    setBulkImportLocaisFile,
    bulkImportLocaisResult,
    handleDownloadBulkLocaisTemplate,
    handleBulkImportLocais,
    resetBulkImportLocaisState,
    handleExportRepresentantes,
    importing,
  } = useHospitalExcel({
    selectedHospital,
    token,
    refreshHospitais,
    refreshLocais,
  });

  // Geo data hook
  const { estados, municipios, loadingCep } = useGeoData({
    selectedHospital,
    editedHospital,
    isEditing,
    setEditedHospital,
  });

  // Delete hospital handler
  const handleDelete = async () => {
    if (!selectedHospital) {
      msg.warning(MESSAGES.HOSPITAL.NO_SELECTION);
      return;
    }

    const confirmed = await confirm({
      title: 'Excluir Hospital',
      message: MESSAGES.HOSPITAL.CONFIRM_DELETE(selectedHospital.nome_fantasia),
      variant: 'danger',
      confirmText: 'Excluir',
    });

    if (!confirmed) return;

    try {
      await estabelecimentoService.deleteEstabelecimento(selectedHospital.id);
      removeHospitalFromList(selectedHospital.id);
      msg.success(MESSAGES.HOSPITAL.DELETED);
    } catch (err: any) {
      console.error('Erro ao excluir hospital:', err);
      const errorMessage = err.response?.data?.detail || MESSAGES.HOSPITAL.DELETE_ERROR;
      msg.error(errorMessage);
    }
  };

  // Story 2.62: reativar hospital desativado (MASTER)
  const handleReactivate = async () => {
    if (!selectedHospital) return;

    const confirmed = await confirm({
      title: 'Reativar Hospital',
      message: `Deseja reativar o estabelecimento "${selectedHospital.nome_fantasia}"?`,
      confirmText: 'Reativar',
    });

    if (!confirmed) return;

    try {
      await reactivateHospital(selectedHospital.id);
      msg.success('Estabelecimento reativado com sucesso');
    } catch (err: any) {
      console.error('Erro ao reativar hospital:', err);
      const errorMessage = err.response?.data?.detail || 'Erro ao reativar estabelecimento';
      msg.error(errorMessage);
    }
  };

  return (
    <>
      <Sidebar />
      <ConfirmDialog />
      <MainLayout>
        <div className="p-6">
          {/* Header with selector and actions */}
          <HospitalHeader
            hospitais={hospitais}
            selectedHospital={selectedHospital}
            filteredHospitais={filteredHospitais}
            loading={loading}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            isMaster={isMaster}
            showInactive={showInactive}
            onShowInactiveChange={setShowInactive}
            onReactivate={handleReactivate}
            showHospitalDropdown={showHospitalDropdown}
            toggleHospitalDropdown={toggleHospitalDropdown}
            closeHospitalDropdown={closeHospitalDropdown}
            onSelectHospital={setSelectedHospital}
            isEditing={isEditing}
            saving={saving}
            onCreateNew={handleCreateNew}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onSave={handleSave}
            onCancel={handleCancel}
            onOpenImportModal={() => {
              resetImportState();
              setShowImportHospitaisModal(true);
            }}
            onExportExcel={handleExportExcel}
          />

          {/* Tabs */}
          <HospitalTabs activeTab={activeTab} setActiveTab={setActiveTab} />

          {/* Content */}
          <div className="flex-1 p-4">
            {activeTab === 'dados-gerais' && (
              <DadosGeraisTab
                currentHospital={currentHospital}
                isEditing={isEditing}
                estados={estados}
                municipios={municipios}
                loadingCep={loadingCep}
                onFieldChange={handleFieldChange}
              />
            )}

            {activeTab === 'locais' && (
              <LocaisContagemTab
                locaisContagem={locaisContagem}
                onAddLocal={() => {
                  if (!selectedHospital) {
                    msg.warning('Selecione um hospital antes de adicionar um local de contagem.');
                    return;
                  }
                  setShowLocalModal(true);
                }}
                onEditLocal={handleEditLocal}
                onDeleteLocal={(localId) => handleDeleteLocal(localId, confirm)}
                onImportLocais={() => {
                  if (!selectedHospital) {
                    msg.warning('Selecione um hospital antes de importar locais de contagem.');
                    return;
                  }
                  resetImportLocaisState();
                  setShowImportLocaisModal(true);
                }}
                onBulkImportLocais={() => {
                  resetBulkImportLocaisState();
                  setShowBulkImportLocaisModal(true);
                }}
                onExportLocais={handleExportLocais}
              />
            )}

            {activeTab === 'representantes' && (
              <RepresentantesTab
                representantes={representantes}
                onExportRepresentantes={handleExportRepresentantes}
              />
            )}

          </div>
        </div>

        {/* Modals */}
        <LocalModal
          show={showLocalModal}
          editingLocal={editingLocal}
          newLocal={newLocal}
          savingLocal={savingLocal}
          onUpdateField={updateLocalField}
          onSave={handleSaveLocal}
          onClose={handleCloseLocalModal}
          // Story 2.34: contatos extras
          contatosExtras={contatosExtras}
          onAddContatoExtra={handleAddContatoExtra}
          onUpdateContatoExtraField={handleUpdateContatoExtraField}
          onRemoveContatoExtra={handleRemoveContatoExtra}
        />

        <ImportHospitalModal
          show={showImportHospitaisModal}
          importFile={importFile}
          importing={importing}
          importResult={importResult}
          onFileChange={setImportFile}
          onDownloadTemplate={handleDownloadTemplate}
          onImport={handleImportHospitais}
          onClose={() => setShowImportHospitaisModal(false)}
        />

        <ImportLocaisModal
          show={showImportLocaisModal}
          importFile={importLocaisFile}
          importing={importing}
          importResult={importLocaisResult}
          onFileChange={setImportLocaisFile}
          onDownloadTemplate={handleDownloadLocaisTemplate}
          onImport={handleImportLocais}
          onClose={() => setShowImportLocaisModal(false)}
        />

        <BulkImportLocaisModal
          show={showBulkImportLocaisModal}
          importFile={bulkImportLocaisFile}
          importing={importing}
          importResult={bulkImportLocaisResult}
          onFileChange={setBulkImportLocaisFile}
          onDownloadTemplate={handleDownloadBulkLocaisTemplate}
          onImport={handleBulkImportLocais}
          onClose={() => setShowBulkImportLocaisModal(false)}
        />

        {showImportRepresentantesModal && selectedHospital && (
          <ImportHospitalRepresentantesModal
            hospitalId={selectedHospital.id}
            hospitalNome={selectedHospital.nome_fantasia || selectedHospital.razao_social}
            onClose={() => setShowImportRepresentantesModal(false)}
            onSuccess={refreshRepresentantes}
          />
        )}
      </MainLayout>
    </>
  );
};

export default HospitaisPage;
